document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const qs = (selector, root = document) => root.querySelector(selector);
const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
const shopRoot = window.Shopify?.routes?.root || '/';

/* ------------------------------------------------------------------ */
/* "More drops coming" splash — shows once per visitor (localStorage),  */
/* never blocks browsing. Skipped entirely on repeat visits or after    */
/* it's been dismissed once, from any page.                             */
/* ------------------------------------------------------------------ */
const SPLASH_SEEN_KEY = 'mettle_splash_seen';
const splash = qs('[data-splash]');
if (splash) {
  const openSplash = () => {
    splash.classList.add('is-open');
    splash.setAttribute('aria-hidden', 'false');
    document.body.classList.add('splash-open');
    qs('#SplashEmail', splash)?.focus();
  };
  const closeSplash = (remember = true) => {
    splash.classList.remove('is-open');
    splash.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('splash-open');
    if (remember) { try { window.localStorage.setItem(SPLASH_SEEN_KEY, '1'); } catch (e) { /* storage unavailable, fine to no-op */ } }
  };

  qsa('[data-splash-dismiss]', splash).forEach((el) => el.addEventListener('click', (event) => {
    event.preventDefault();
    closeSplash();
  }));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && splash.classList.contains('is-open')) closeSplash();
  });

  const alreadySubmitted = !!qs('[data-splash-success]', splash);
  let alreadySeen = false;
  try { alreadySeen = window.localStorage.getItem(SPLASH_SEEN_KEY) === '1'; } catch (e) { /* storage unavailable, just show it */ }

  if (alreadySubmitted) {
    // Full-page form post just redirected back here — show the
    // confirmation once, then never again (don't wait on the timeout).
    try { window.localStorage.setItem(SPLASH_SEEN_KEY, '1'); } catch (e) { /* fine to no-op */ }
    openSplash();
  } else if (!alreadySeen) {
    window.setTimeout(openSplash, 400);
  }
}

const revealElements = qsa('[data-reveal]');
if ('IntersectionObserver' in window) {
  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: .12 });
  revealElements.forEach((element) => revealObserver.observe(element));
} else {
  revealElements.forEach((element) => element.classList.add('is-visible'));
}

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
}[character]));

const formatMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'EGP';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

/* ------------------------------------------------------------------ */
/* Shared cart helpers — used by the PDP form, sticky bars, quick view */
/* ------------------------------------------------------------------ */
async function postCartAdd(variantId, quantity = 1) {
  const response = await fetch(`${shopRoot}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.description || payload.message || 'Unable to add item');
  }
  return response.json();
}

function goToCheckout() {
  window.location.href = `${shopRoot}checkout`;
}

const header = qs('[data-header]');
if (header) {
  window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 24), { passive: true });
  const toggle = qs('.menu-toggle', header);
  const nav = qs('.primary-nav', header);
  toggle?.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    nav?.classList.toggle('is-open', !open);
  });
}

qsa('[data-product-rail]').forEach((rail) => {
  const section = rail.closest('.shop-browser');
  const progress = qs('[data-rail-progress]', section);
  const update = () => {
    const available = rail.scrollWidth - rail.clientWidth;
    const ratio = available > 0 ? rail.scrollLeft / available : 0;
    if (progress) progress.style.transform = `scaleX(${Math.max(.08, ratio)})`;
  };
  qs('[data-rail-prev]', section)?.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * .72, behavior: 'smooth' }));
  qs('[data-rail-next]', section)?.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * .72, behavior: 'smooth' }));
  rail.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
});

/* ------------------------------------------------------------------ */
/* Shop category filter — client-side, no page navigation             */
/* ------------------------------------------------------------------ */
qsa('[data-category-filter]').forEach((nav) => {
  const shopBrowser = nav.closest('.shop-browser');
  const rail = qs('[data-product-rail]', shopBrowser);
  const emptyState = qs('[data-rail-empty]', shopBrowser);
  const countLabel = qs('[data-rail-count]', shopBrowser);
  if (!rail) return;
  const cards = qsa('.product-card', rail);
  const buttons = qsa('button[data-filter]', nav);

  const applyFilter = (value) => {
    let visible = 0;
    cards.forEach((card) => {
      const categories = (card.dataset.categories || '').split(',').filter(Boolean);
      const match = value === 'all' || categories.includes(value);
      card.hidden = !match;
      if (match) visible += 1;
    });
    if (emptyState) emptyState.hidden = visible !== 0;
    if (countLabel) countLabel.textContent = String(visible).padStart(3, '0');
    rail.scrollTo({ left: 0, behavior: 'instant' in document.documentElement.style ? 'instant' : 'auto' });
  };

  buttons.forEach((button) => button.addEventListener('click', () => {
    buttons.forEach((other) => {
      other.classList.toggle('is-active', other === button);
      other.setAttribute('aria-pressed', String(other === button));
    });
    applyFilter(button.dataset.filter);
  }));
});

/* ------------------------------------------------------------------ */
/* Quick view modal                                                    */
/* ------------------------------------------------------------------ */
const quickView = qs('[data-quick-view]');
const quickViewScrim = qs('[data-quick-view-scrim]');
const quickViewBody = qs('[data-quick-view-body]');
let quickViewLastTrigger = null;

const buildQuickViewMarkup = (product) => {
  const firstVariant = product.variants.find((variant) => variant.available) || product.variants[0];
  const hasOptions = product.variants.length > 1;
  const images = product.images && product.images.length ? product.images : (product.featured_image ? [product.featured_image] : []);

  const variantButtons = hasOptions
    ? `<div class="variant-grid quick-view__variants" role="group" aria-label="Available configurations">${product.variants.map((variant) => `
        <button class="variant-option${variant.id === firstVariant.id ? ' is-selected' : ''}" type="button" data-variant-option data-variant-id="${variant.id}" data-price="${escapeHtml(formatMoney(variant.price))}" aria-pressed="${variant.id === firstVariant.id}"${!variant.available ? ' disabled' : ''}>${escapeHtml(variant.title === 'Default Title' ? variant.name : variant.title)}${!variant.available ? ' — SOLD OUT' : ''}</button>`).join('')}</div>`
    : '';

  const gallery = images.length
    ? `<div class="product-gallery__track" data-gallery-track>
        ${images.map((src, i) => `
          <div class="product-gallery__slide"${i === 0 ? ' data-gallery-active' : ''} data-gallery-slide>
            <img class="product-gallery__image" src="${escapeHtml(src)}&width=1400" alt="${escapeHtml(product.title)}" loading="${i === 0 ? 'eager' : 'lazy'}">
          </div>`).join('')}
      </div>
      ${images.length > 1 ? `
        <button type="button" class="product-gallery__arrow product-gallery__arrow--prev" aria-label="Previous image" data-gallery-prev>‹</button>
        <button type="button" class="product-gallery__arrow product-gallery__arrow--next" aria-label="Next image" data-gallery-next>›</button>` : ''}`
    : '<div class="product-media product-media--empty"><span class="utility">PRODUCT IMAGE PENDING</span></div>';

  return `
    <figure class="quick-view__media" data-product-gallery data-gallery-no-zoom>
      ${gallery}
    </figure>
    <div class="quick-view__console">
      <div class="quick-view__scroll">
        <p class="utility quick-view__eyebrow">METTLE / FIELD EQUIPMENT</p>
        <h2 id="QuickViewTitle">${escapeHtml(product.title)}</h2>
        ${product.description ? `<div class="product-description rte quick-view__description">${product.description}</div>` : ''}
        <form class="product-form" data-product-form data-quick-view-form>
          <input type="hidden" name="id" value="${firstVariant.id}" data-variant-select-hidden>
          ${variantButtons}
          <input type="hidden" name="quantity" value="1">
        </form>
      </div>
      <div class="quick-view__sticky-bar">
        <p class="product-price" data-product-price>${escapeHtml(formatMoney(firstVariant.price))}</p>
        <div class="quick-view__sticky-actions">
          <button class="product-submit product-submit--ghost" type="button" data-quick-view-add${!firstVariant.available ? ' disabled' : ''}><span>${firstVariant.available ? 'ADD TO BAG' : 'SOLD OUT'}</span></button>
          <button class="product-submit" type="button" data-quick-view-buy-now${!firstVariant.available ? ' disabled' : ''}><span>BUY NOW</span><span aria-hidden="true">→</span></button>
        </div>
      </div>
      <p class="product-form__status utility" data-product-form-status aria-live="polite"></p>
    </div>`;
};

const trapFocusIn = (event, container) => {
  const focusable = qsa('button, a[href], input, select, textarea', container).filter((el) => !el.disabled && el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
};

const trapFocus = (event) => {
  if (event.key !== 'Tab') return;
  if (quickView?.classList.contains('is-open')) trapFocusIn(event, quickView);
};

const closeQuickView = () => {
  if (!quickView) return;
  quickView.classList.remove('is-open');
  quickView.setAttribute('aria-hidden', 'true');
  quickViewScrim?.classList.remove('is-open');
  document.body.classList.remove('quick-view-open');
  window.setTimeout(() => { if (quickViewBody) quickViewBody.innerHTML = '<div class="quick-view__loading utility" data-quick-view-loading>LOADING / STAND BY</div>'; }, 260);
  quickViewLastTrigger?.focus();
};

const openQuickView = async (handle, trigger) => {
  if (!quickView || !quickViewBody) return;
  quickViewLastTrigger = trigger || null;
  quickView.classList.add('is-open');
  quickView.setAttribute('aria-hidden', 'false');
  quickViewScrim?.classList.add('is-open');
  document.body.classList.add('quick-view-open');
  qs('.quick-view__close', quickView)?.focus();
  try {
    const response = await fetch(`${shopRoot}products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load product');
    const product = await response.json();
    quickViewBody.innerHTML = buildQuickViewMarkup(product);
    initGallery(quickViewBody);

    const form = qs('[data-quick-view-form]', quickViewBody);
    const hiddenInput = qs('[data-variant-select-hidden]', form);
    const price = qs('[data-product-price]', quickViewBody);
    const addButton = qs('[data-quick-view-add]', quickViewBody);
    const buyNowButton = qs('[data-quick-view-buy-now]', quickViewBody);
    const status = qs('[data-product-form-status]', quickViewBody);

    qsa('[data-variant-option]', form).forEach((button) => button.addEventListener('click', () => {
      hiddenInput.value = button.dataset.variantId;
      if (price) price.textContent = button.dataset.price;
      const disabled = button.hasAttribute('disabled');
      if (addButton) {
        addButton.disabled = disabled;
        addButton.querySelector('span').textContent = disabled ? 'SOLD OUT' : 'ADD TO BAG';
      }
      if (buyNowButton) buyNowButton.disabled = disabled;
      qsa('[data-variant-option]', form).forEach((other) => {
        other.classList.toggle('is-selected', other === button);
        other.setAttribute('aria-pressed', String(other === button));
      });
    }));

    addButton?.addEventListener('click', async () => {
      addButton.disabled = true;
      if (status) status.textContent = 'ADDING / STAND BY';
      try {
        await postCartAdd(hiddenInput.value, 1);
        await getCart();
        if (status) status.textContent = 'ADDED / BAG UPDATED';
        window.setTimeout(() => { closeQuickView(); setDrawer(true); }, 320);
      } catch (error) {
        if (status) status.textContent = error.message.toUpperCase();
        addButton.disabled = false;
      }
    });

    buyNowButton?.addEventListener('click', async () => {
      buyNowButton.disabled = true;
      if (addButton) addButton.disabled = true;
      if (status) status.textContent = 'TAKING YOU TO CHECKOUT / STAND BY';
      try {
        await postCartAdd(hiddenInput.value, 1);
        goToCheckout();
      } catch (error) {
        if (status) status.textContent = error.message.toUpperCase();
        buyNowButton.disabled = false;
        if (addButton) addButton.disabled = false;
      }
    });
  } catch (error) {
    quickViewBody.innerHTML = `<p class="quick-view__loading utility">${escapeHtml(error.message.toUpperCase())}</p>`;
  }
};

/* The quick-view popup card was hiding the real product page (full-bleed
   gallery, proper fit notes, back-to-shop) behind a small centered modal —
   always send shoppers to the real canonical product page instead, on
   every viewport. Anchor triggers (the card image/title link) just use
   their normal href; only the button trigger (no href) needs a JS nav. */
qsa('[data-quick-view-trigger]').forEach((trigger) => trigger.addEventListener('click', (event) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
  if (trigger.tagName === 'A') return;
  event.preventDefault();
  window.location.href = `${shopRoot}products/${trigger.dataset.productHandle}`;
}));

/* ------------------------------------------------------------------ */
/* Quick Add — direct add for single-variant products. Re-initialized  */
/* on shopify:section:load so cards re-rendered in the Theme Editor    */
/* (e.g. after a collection/setting change) keep working.              */
/* ------------------------------------------------------------------ */
const initQuickAdd = (root = document) => {
  qsa('[data-quick-add-direct]', root).forEach((button) => {
    if (button.dataset.quickAddBound) return;
    button.dataset.quickAddBound = 'true';
    const label = button.textContent;
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      button.disabled = true;
      button.textContent = 'ADDING…';
      try {
        await postCartAdd(button.dataset.variantId, 1);
        await getCart();
        button.textContent = 'ADDED';
        setDrawer(true);
        window.setTimeout(() => { button.textContent = label; button.disabled = false; }, 1400);
      } catch (error) {
        button.textContent = 'TRY AGAIN';
        button.disabled = false;
        window.setTimeout(() => { button.textContent = label; }, 1800);
      }
    });
  });
};

initQuickAdd();
document.addEventListener('shopify:section:load', (event) => initQuickAdd(event.target));

/* ------------------------------------------------------------------ */
/* PDP gallery — swipe (native scroll-snap, no gesture library),       */
/* thumbnail nav, tap-to-zoom. Built against product.media, so it      */
/* works for however many images a product actually has.               */
/* ------------------------------------------------------------------ */
const initGallery = (root = document) => {
  qsa('[data-product-gallery]', root).forEach((gallery) => {
    if (gallery.dataset.galleryBound) return;
    gallery.dataset.galleryBound = 'true';
    const track = qs('[data-gallery-track]', gallery);
    const slides = qsa('[data-gallery-slide]', gallery);
    const thumbs = qsa('[data-gallery-thumb]', gallery);
    const prevButton = qs('[data-gallery-prev]', gallery);
    const nextButton = qs('[data-gallery-next]', gallery);
    if (!track || !slides.length) return;

    let currentIndex = 0;

    const setActive = (index) => {
      currentIndex = index;
      slides.forEach((slide, i) => slide.toggleAttribute('data-gallery-active', i === index));
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', String(i === index));
      });
    };

    const goTo = (index) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, index));
      slides[clamped]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      setActive(clamped);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryIndex))));
    prevButton?.addEventListener('click', () => goTo(currentIndex - 1));
    nextButton?.addEventListener('click', () => goTo(currentIndex + 1));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) setActive(slides.indexOf(entry.target)); });
      }, { root: track, threshold: .6 });
      slides.forEach((slide) => observer.observe(slide));
    }

    if (!gallery.hasAttribute('data-gallery-no-zoom')) {
      slides.forEach((slide) => {
        qs('.product-gallery__image', slide)?.addEventListener('click', () => slide.classList.toggle('is-zoomed'));
      });
    }
  });
};

initGallery();
document.addEventListener('shopify:section:load', (event) => initGallery(event.target));

/* Homepage hero: auto-cycle through the featured product's photos, one
   crossfade every N seconds (data-spotlight-interval, default 8000ms).
   Pauses when the tab is hidden so it doesn't burn cycles in the background,
   and stops entirely for anyone who asked for reduced motion. */
const initSpotlightCarousel = (root = document) => {
  qsa('[data-spotlight-carousel]', root).forEach((carousel) => {
    if (carousel.dataset.spotlightBound) return;
    carousel.dataset.spotlightBound = 'true';
    const slides = qsa('[data-spotlight-slide]', carousel);
    if (slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const interval = Number(carousel.dataset.spotlightInterval) || 8000;
    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = 0;
    let timer = null;

    const show = (next) => {
      slides[index]?.classList.remove('is-active');
      index = next;
      slides[index]?.classList.add('is-active');
    };

    const tick = () => show((index + 1) % slides.length);

    const start = () => { if (!timer) timer = window.setInterval(tick, interval); };
    const stop = () => { if (timer) { window.clearInterval(timer); timer = null; } };

    start();
    document.addEventListener('visibilitychange', () => { document.hidden ? stop() : start(); });
  });
};

initSpotlightCarousel();
document.addEventListener('shopify:section:load', (event) => initSpotlightCarousel(event.target));

/* Desktop PDP: show the full photo uncropped (object-fit:contain) instead
   of the tight cover-crop used on mobile — set as an inline style (highest
   specificity, always wins) rather than relying purely on the stylesheet,
   since it needs to be bulletproof against any other object-fit rule. */
const applyDesktopGalleryFit = () => {
  const isDesktop = window.matchMedia('(min-width: 761px)').matches;
  qsa('.product-stage__media img').forEach((img) => {
    if (isDesktop) {
      img.style.objectFit = 'contain';
      img.style.objectPosition = img.closest('.product-gallery__slide--wide') ? 'center' : 'center bottom';
    } else {
      img.style.removeProperty('object-fit');
      img.style.removeProperty('object-position');
    }
  });
};
applyDesktopGalleryFit();
window.addEventListener('resize', applyDesktopGalleryFit);
document.addEventListener('shopify:section:load', applyDesktopGalleryFit);

/* "Back to shop" should return to wherever the customer actually came from
   (e.g. scroll position in the homepage grid) rather than always jumping to
   a fixed URL — real browser back when we got here from within the site,
   falling back to the home page's shop section for direct/shared links. */
qs('[data-back-to-shop]')?.addEventListener('click', (event) => {
  const cameFromSite = document.referrer && new URL(document.referrer).origin === window.location.origin;
  if (cameFromSite && window.history.length > 1) {
    event.preventDefault();
    window.history.back();
  }
});

qs('[data-quick-view-close]')?.addEventListener('click', closeQuickView);
quickViewScrim?.addEventListener('click', closeQuickView);
// The quick-view wrapper itself is full-screen (inset:0) so it sits above
// the scrim and eats clicks meant for it — close whenever the click lands
// on the wrapper's own padding area, outside the centered card.
quickView?.addEventListener('click', (event) => {
  if (event.target === quickView) closeQuickView();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && quickView?.classList.contains('is-open')) closeQuickView();
  trapFocus(event);
});

const drawer = qs('[data-cart-drawer]');
const scrim = qs('[data-drawer-scrim]');
const cartItems = qs('[data-cart-items]');
const cartTotal = qs('[data-cart-total]');
const checkoutButton = qs('.cart-drawer .checkout-button');

const setDrawer = (open) => {
  if (!drawer || !scrim) return;
  drawer.classList.toggle('is-open', open);
  drawer.setAttribute('aria-hidden', String(!open));
  scrim.hidden = !open;
  document.body.classList.toggle('drawer-open', open);
  if (open) qs('[data-cart-close]', drawer)?.focus();
};

const renderCart = (cart) => {
  qsa('[data-cart-count]').forEach((node) => { node.textContent = String(cart.item_count).padStart(2, '0'); });
  if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);
  if (checkoutButton) checkoutButton.disabled = cart.item_count === 0;
  if (!cartItems) return;
  if (!cart.items.length) {
    cartItems.innerHTML = '<p class="cart-empty utility">YOUR BAG IS EMPTY.</p>';
    return;
  }
  cartItems.innerHTML = cart.items.map((item) => `
    <article class="cart-line" data-line-key="${escapeHtml(item.key)}">
      <a href="${escapeHtml(item.url)}" class="cart-line__image">${item.image ? `<img src="${escapeHtml(item.image)}&width=240" width="120" alt="${escapeHtml(item.product_title)}">` : ''}</a>
      <div class="cart-line__info">
        <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>
        <span class="utility">${escapeHtml(item.variant_title || '')}</span>
        <span>${formatMoney(item.final_line_price)}</span>
        <div class="quantity"><button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}">−</button><span>${item.quantity}</span><button type="button" data-cart-quantity="${item.quantity + 1}">+</button></div>
      </div>
    </article>`).join('');
};

const getCart = async () => {
  const response = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load bag');
  const cart = await response.json();
  renderCart(cart);
  return cart;
};

qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
  setDrawer(true);
  try { await getCart(); } catch (error) { console.error(error); }
}));
qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setDrawer(false)));
scrim?.addEventListener('click', () => setDrawer(false));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') setDrawer(false);
  if (event.key === 'Tab' && drawer?.classList.contains('is-open')) trapFocusIn(event, drawer);
});

/* ------------------------------------------------------------------ */
/* Mobile filter/sort bottom sheet — same #FacetFiltersForm as desktop, */
/* only the open/close chrome is new.                                  */
/* ------------------------------------------------------------------ */
const filterSheet = qs('[data-filter-sheet]');
const filterSheetScrim = qs('[data-filter-sheet-scrim]');
const filterSheetOpeners = qsa('[data-filter-sheet-open]');

const setFilterSheet = (open) => {
  if (!filterSheet || !filterSheetScrim) return;
  filterSheet.classList.toggle('is-open', open);
  filterSheetScrim.hidden = !open;
  document.body.classList.toggle('filter-sheet-open', open);
  filterSheetOpeners.forEach((button) => button.setAttribute('aria-expanded', String(open)));
  if (open) qs('[data-filter-sheet-close]', filterSheet)?.focus();
  else filterSheetOpeners[0]?.focus();
};

filterSheetOpeners.forEach((button) => button.addEventListener('click', () => setFilterSheet(true)));
qs('[data-filter-sheet-close]')?.addEventListener('click', () => setFilterSheet(false));
filterSheetScrim?.addEventListener('click', () => setFilterSheet(false));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && filterSheet?.classList.contains('is-open')) setFilterSheet(false);
  if (event.key === 'Tab' && filterSheet?.classList.contains('is-open')) trapFocusIn(event, filterSheet);
});

cartItems?.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-quantity]');
  const line = event.target.closest('[data-line-key]');
  if (!button || !line) return;
  button.disabled = true;
  try {
    const response = await fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.dataset.lineKey, quantity: Number(button.dataset.cartQuantity) }),
    });
    if (!response.ok) throw new Error('Unable to update bag');
    renderCart(await response.json());
  } catch (error) {
    button.disabled = false;
    console.error(error);
  }
});

qsa('[data-product-form]').forEach((form) => {
  const productRoot = form.closest('[data-product-root]');
  const status = qs('[data-product-form-status]', form);
  const submit = qs('[data-add-to-cart]', form);
  const buyNow = qs('[data-buy-now]', productRoot);
  const variant = qs('[data-variant-select]', form);
  const variantButtons = qsa('[data-variant-option]', form);
  const price = qs('[data-product-price]', productRoot);

  const stickyBar = qs('[data-product-sticky-bar]');
  const stickyPrice = qs('[data-sticky-price]', stickyBar);
  const stickyAdd = qs('[data-sticky-add-to-cart]', stickyBar);
  const stickyAddLabel = qs('[data-sticky-add-label]', stickyBar);
  const stickyBuyNow = qs('[data-sticky-buy-now]', stickyBar);

  const syncSticky = (available, priceText) => {
    if (stickyPrice && priceText) stickyPrice.textContent = priceText;
    if (stickyAdd) {
      stickyAdd.disabled = !available;
      if (stickyAddLabel) stickyAddLabel.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';
    }
    if (stickyBuyNow) stickyBuyNow.disabled = !available;
  };

  variant?.addEventListener('change', () => {
    const option = variant.selectedOptions[0];
    const available = !option?.disabled;
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (buyNow) buyNow.disabled = !available;
    variantButtons.forEach((button) => {
      const selected = button.dataset.variantId === variant.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    syncSticky(available, option?.dataset.price);
  });
  variantButtons.forEach((button) => button.addEventListener('click', () => {
    if (!variant) return;
    variant.value = button.dataset.variantId;
    variant.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    if (status) status.textContent = 'ADDING / STAND BY';
    try {
      await postCartAdd(variant.value, 1);
      await getCart();
      setDrawer(true);
      if (status) status.textContent = 'ADDED / BAG UPDATED';
    } catch (error) {
      if (status) status.textContent = error.message.toUpperCase();
    } finally {
      submit.disabled = false;
    }
  });

  buyNow?.addEventListener('click', async () => {
    buyNow.disabled = true;
    if (submit) submit.disabled = true;
    if (status) status.textContent = 'TAKING YOU TO CHECKOUT / STAND BY';
    try {
      await postCartAdd(variant.value, 1);
      goToCheckout();
    } catch (error) {
      if (status) status.textContent = error.message.toUpperCase();
      buyNow.disabled = false;
      if (submit) submit.disabled = false;
    }
  });

  stickyAdd?.addEventListener('click', async () => {
    stickyAdd.disabled = true;
    if (stickyBuyNow) stickyBuyNow.disabled = true;
    try {
      await postCartAdd(variant.value, 1);
      await getCart();
      setDrawer(true);
    } catch (error) {
      console.error(error);
    } finally {
      stickyAdd.disabled = false;
      if (stickyBuyNow) stickyBuyNow.disabled = false;
    }
  });

  stickyBuyNow?.addEventListener('click', async () => {
    stickyBuyNow.disabled = true;
    if (stickyAdd) stickyAdd.disabled = true;
    try {
      await postCartAdd(variant.value, 1);
      goToCheckout();
    } catch (error) {
      console.error(error);
      stickyBuyNow.disabled = false;
      if (stickyAdd) stickyAdd.disabled = false;
    }
  });

  if (stickyBar) {
    const primaryAction = qs('.product-console__action', productRoot);
    if (primaryAction && 'IntersectionObserver' in window) {
      const stickyObserver = new IntersectionObserver(([entry]) => {
        const showSticky = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', showSticky);
        stickyBar.setAttribute('aria-hidden', String(!showSticky));
      }, { threshold: 0 });
      stickyObserver.observe(primaryAction);
    }
  }
});
